import { useTranslation } from "react-i18next";

/**
 * Compact paginator. Pages are 0-indexed (matches Spring Data Page).
 * Renders nothing when there's nothing to paginate.
 */
export function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (next: number) => void;
}) {
  const { t } = useTranslation();
  if (totalPages <= 1) return null;

  const pages = visiblePages(page, totalPages);
  return (
    <div style={wrap}>
      <button type="button" onClick={() => onChange(page - 1)} disabled={page === 0} style={btn}>{t("pagination.previous")}</button>
      {pages.map((p, i) =>
        p === "…" ? (
          <span key={`ell-${i}`} style={ellipsis}>…</span>
        ) : (
          <button
            type="button"
            key={p}
            onClick={() => onChange(p)}
            style={{ ...btn, ...(p === page ? activeBtn : {}) }}
          >
            {p + 1}
          </button>
        ),
      )}
      <button type="button" onClick={() => onChange(page + 1)} disabled={page >= totalPages - 1} style={btn}>{t("pagination.next")}</button>
    </div>
  );
}

/**
 * Returns at most 7 entries: first, last, current ±1, and "…" placeholders.
 * Exported for unit testing.
 */
export function visiblePages(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const result: (number | "…")[] = [];
  const set = new Set<number>([0, total - 1, current, current - 1, current + 1]);
  const sorted = [...set].filter((n) => n >= 0 && n < total).sort((a, b) => a - b);
  let prev = -1;
  for (const n of sorted) {
    if (prev !== -1 && n - prev > 1) result.push("…");
    result.push(n);
    prev = n;
  }
  return result;
}

const wrap: React.CSSProperties = { display: "flex", gap: 4, alignItems: "center", justifyContent: "center", marginTop: 16 };
const btn: React.CSSProperties = {
  padding: "6px 10px",
  background: "var(--surface)",
  border: "1px solid var(--border-strong)",
  borderRadius: 6,
  fontSize: 13,
  cursor: "pointer",
  color: "var(--ink-2)",
};
const activeBtn: React.CSSProperties = { background: "var(--brand-primary)", color: "#fff", borderColor: "var(--brand-primary)" };
const ellipsis: React.CSSProperties = { color: "var(--ink-4)", padding: "0 4px", fontSize: 13 };
